import { Grid } from '@mui/material';
import clsx from 'clsx';
import { Button, Stack } from 'components';
import dayjs from 'dayjs';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useState } from 'react';
import { getAddendums, getUsersImageNotes } from 'store/clientProfile/clientProfileActions';
import {
  Addendum as AddendumType,
  ClientProfileContentItem
} from 'store/clientProfile/clientProfileSlice';
import {
  howDidYouReachThemData,
  typesOfSupportData,
  whatTypeOfPlaceWereYouInData,
  whoDidYouReachData,
  whoWereYouTryingToReachData
} from 'store/clients/clientsSlice';
import { useAppDispatch, useAppSelector } from 'store/hooks';

import { getNoteTypeData } from '../noteEdit/fields/noteType/noteType';
import Transcript from '../noteEdit/transcript/transcript';
import { Addendum } from './addendum';
import { AddendumForm } from './addendumForm';
import style from './noteView.module.scss';

type Props = {
  note: ClientProfileContentItem;
};

const TimeSpent = ({ note }: Props) => {
  return (
    <Stack display="flex" gap="8px" flex="1">
      <p className={style.label}>Time Spent:</p>
      <p className={style.text}>{note.timeSpentMin} Mins</p>
    </Stack>
  );
};

const NextContact = ({ note }: Props) => {
  return (
    <Stack display="flex" gap="8px" flex="1">
      <p className={style.label}>Next Contact:</p>
      <p className={style.text}>
        {note.nextContact && dayjs(note.nextContact).isValid()
          ? dayjs(note?.nextContact).format('MM/DD/YYYY')
          : ''}
      </p>
    </Stack>
  );
};

const TypeOfSupport = ({ note }: Props) => {
  const sortValues = (val: string[] | string, sortOrder: any[]) => {
    if (Array.isArray(val)) {
      return sortOrder
        .filter((item) => val.includes(item.id.toString()))
        .map((item) => item.name)
        .join(', ');
    } else {
      return val;
    }
  };

  return (
    <Stack display="flex" gap="8px" flex="1">
      <p className={style.label}>What type of support did you provide?</p>
      <p className={style.text}>{sortValues(note.typeOfSupport, typesOfSupportData)}</p>
    </Stack>
  );
};

const Sites = ({ note }: Props) => {
  return (
    <Stack display="flex" gap="8px" flex="1">
      <p className={style.label}>What specific site?</p>
      <p className={style.text}>{note.siteName}</p>
    </Stack>
  );
};

const NoteType = ({ note }: Props) => {
  const noteTypeData = getNoteTypeData({ canUpdateManagerReview: true });
  const noteType = noteTypeData.find((n) => n.id === note.noteType);

  return (
    <Stack display="flex" gap="8px" flex="1">
      <p className={style.label}>Note Type:</p>
      <p className={style.text}>{noteType?.name}</p>
    </Stack>
  );
};

const StartTime = ({ note }: Props) => {
  const timeFormatedValue =
    dayjs(note?.startTimeOfEncounter, ['hh:mm A', 'HH:mm:ss'], true).format('h:mm A') !==
    'Invalid Date'
      ? dayjs(note?.startTimeOfEncounter, ['hh:mm A', 'HH:mm:ss'], true).format('h:mm A')
      : '';

  return (
    <Stack display="flex" gap="8px" flex="1">
      <p className={style.label}>Start Time:</p>
      <p className={style.text}>{timeFormatedValue}</p>
    </Stack>
  );
};

const DateOfEncounter = ({ note }: Props) => {
  return (
    <Stack display="flex" gap="8px" flex="1">
      <p className={style.label}>Date of Encounter:</p>
      <p className={style.text}>{dayjs(note?.dateOfEncounter).format('MM/DD/YYYY')}</p>
    </Stack>
  );
};

const ZCodes = ({ note }: Props) => {
  return (
    <Stack display="flex" gap="8px" flex="1">
      <p className={style.label}>SDOH Z codes:</p>
      <Stack display="flex" flexDirection="column" gap="12px">
        {note.selectedZCodes?.map((selectedZCode) => {
          return (
            <Stack display="flex" flexDirection="column" gap="4px">
              <p className={style.text}>
                {selectedZCode.code} - {selectedZCode.description}
              </p>
              <div>
                {selectedZCode.selectedExplanations.map((e) => {
                  return <p className={style.text}>{e.explanation}</p>;
                })}
              </div>
            </Stack>
          );
        })}
      </Stack>
    </Stack>
  );
};

const Manager = ({ note }: Props) => {
  const managers = useAppSelector((state) => state.clients.managers.data);
  const manager = managers.find((m) => m.id.toString() === note.managerId?.toString());

  return (
    <Stack display="flex" gap="8px" flex="1">
      <p className={style.label}>Manager:</p>
      <p className={style.text}>{manager?.name}</p>
    </Stack>
  );
};

const Chw = ({ note }: Props) => {
  const chws = useAppSelector((state) => state.clients.chws.data);
  const chw = chws.find((c) => c.id.toString() === note.chwId?.toString());

  return (
    <Stack display="flex" gap="8px" flex="1">
      <p className={style.label}>CHW:</p>
      <p className={style.text}>{chw?.name}</p>
    </Stack>
  );
};

const WhichProvider = ({ note }: Props) => {
  const providers = useAppSelector((state) => state.clients.billingProviders.data);
  const provider = providers.find(
    (p) => p.id.toString() === note.billingProviderId?.toString()
  )?.name;

  return (
    <Stack display="flex" gap="8px" flex="1">
      <p className={style.label}>Which provider?</p>
      <p className={style.text}>{provider}</p>
    </Stack>
  );
};

const ManagerReviewProvider = ({ note }: Props) => {
  const providers = useAppSelector((state) => state.clients.billingProviders.data);
  const provider = providers.find(
    (p) => p.id.toString() === note.billingProviderId?.toString()
  )?.name;

  return (
    <Stack display="flex" gap="8px" flex="1">
      <p className={style.label}>Provider:</p>
      <p className={style.text}>{provider}</p>
    </Stack>
  );
};

const OtherProviderName = ({ note }: Props) => {
  return (
    <Stack display="flex" gap="8px" flex="1">
      <p className={style.label}>Provider Name:</p>
      <p className={style.text}>{note.otherProviderName}</p>
    </Stack>
  );
};

const DocumentationTime = ({ note }: Props) => {
  const getDocumentationTime = (documentationTime: number | null) => {
    if (documentationTime == null || documentationTime === 0) {
      return '';
    }
    return `${documentationTime} ${documentationTime > 1 ? 'mins' : 'min'}`;
  };

  return (
    <Stack display="flex" gap="8px" flex="1">
      <p className={style.label}>Documentation Time:</p>
      <p className={style.text}>{getDocumentationTime(note.documentationTime)}</p>
    </Stack>
  );
};

const WhoDidYouReach = ({ note }: Props) => {
  const whoDidYouReachNames = note.whoDidYouReach
    .map((w) => whoDidYouReachData.find((wD) => wD.id === w)?.name)
    .join(', ');

  return (
    <Stack display="flex" gap="8px" flex="1">
      <p className={style.label}>Who did you reach?</p>
      <p className={style.text}>{whoDidYouReachNames}</p>
    </Stack>
  );
};

const HowDidYouReachThem = ({ note }: Props) => {
  const howDidYouReachThemName = howDidYouReachThemData.find(
    (h) => h.id === note.howDidYouReachThem
  )?.name;

  return (
    <Stack display="flex" gap="8px" flex="1">
      <p className={style.label}>How did you reach them?</p>
      <p className={style.text}>{howDidYouReachThemName}</p>
    </Stack>
  );
};

const WhatTypeOfPlaceWereYouIn = ({ note }: Props) => {
  const whatTypeOfPlaceWereYouInName = whatTypeOfPlaceWereYouInData.find(
    (w) => w.id === note.whatTypeOfPlaceWereYouIn
  )?.name;

  return (
    <Stack display="flex" gap="8px" flex="1">
      <p className={style.label}>What type of place were you in?</p>
      <p className={style.text}>{whatTypeOfPlaceWereYouInName}</p>
    </Stack>
  );
};

const WhoWereYouTryingToReach = ({ note }: Props) => {
  const whoWereYouTryingToReachName = whoWereYouTryingToReachData.find(
    (w) => w.id === note.whoWereYouTryingToReach
  )?.name;

  return (
    <>
      <Stack display="flex" gap="8px" flex="1">
        <p className={style.label}>Who were you trying to reach?</p>
        <p className={style.text}>{whoWereYouTryingToReachName}</p>
      </Stack>
    </>
  );
};

const HowDidYouTryThem = ({ note }: Props) => {
  const howDidYouTryThemName = howDidYouReachThemData.find(
    (h) => h.id === note.howDidYouTryThem
  )?.name;

  return (
    <Stack display="flex" gap="8px" flex="1">
      <p className={style.label}>How did you try them?</p>
      <p className={style.text}>{howDidYouTryThemName}</p>
    </Stack>
  );
};
const NoteView = ({ note }: Props) => {
  const addendums = useAppSelector((state) => state.clientProfile.addendums.data);
  const [showAddendum, setShowAddendum] = useState(false);
  const dispatch = useAppDispatch();
  const { aiScribe: enableAiScribe } = useFlags();
  const sitesEnabled = useAppSelector((state) => state.clients.client.data?.enableProgramSites);

  useEffect(() => {
    dispatch(getAddendums({ noteId: note.id.toString() })).then((res: any) => {
      if (!res.error && res.payload.content.length > 0) {
        //TODO need to optimize
        res.payload.content.map((addendum: AddendumType) => {
          if (addendum.author.profileImageKey) {
            dispatch(
              getUsersImageNotes({
                profileImageKey: addendum.author.profileImageKey,
                noteId: addendum.id.toString(),
                noteAddendums: true
              })
            );
          }
        });
      }
    });
  }, []);

  const isManagerReview = note.noteType === 'MANAGER_REVIEW';

  if (isManagerReview) {
    return (
      <Stack mt="36px" display="flex" gap="22px">
        <Stack display="flex" flexDirection="row">
          <NoteType note={note} />
          <DateOfEncounter note={note} />
        </Stack>
        <Stack display="flex" flexDirection="row">
          <TimeSpent note={note} />
          <Manager note={note} />
        </Stack>
        <Stack display="flex" flexDirection="row">
          <Chw note={note} />
          <ManagerReviewProvider note={note} />
        </Stack>
        <div className={style.dottedLine} />
        <Stack display="flex" flexDirection="row">
          <Stack display="flex" gap="8px" flex="1">
            <p className={style.label}>Note:</p>
            <p className={style.text}>{note.note}</p>
          </Stack>
        </Stack>
        <DocumentationTime note={note} />
      </Stack>
    );
  }

  const showWhoWereYouTryingToReach =
    note.whoDidYouReach?.length > 0 && note.whoDidYouReach?.includes('NO_ONE');

  const showHowDidYouTryThem =
    note.whoWereYouTryingToReach?.length > 0 &&
    note.whoWereYouTryingToReach !== 'WORK_ON_MY_CLIENTS_BEHALF' &&
    note.howDidYouTryThem !== '';

  const showWhichProvider =
    note.whoDidYouReach?.length > 0 && note.whoDidYouReach?.includes('PROVIDER');

  const showOtherProviderName = showWhichProvider && note.billingProviderId.toString() === '-1';

  const showHowDidYouReachThem =
    note.whoDidYouReach?.length > 0 && !note.whoDidYouReach?.includes('NO_ONE');

  const showWhatTypeOfPlaceWereYouIn =
    (showHowDidYouTryThem && note.howDidYouTryThem?.includes('IN_PERSON')) ||
    (showHowDidYouReachThem && note.howDidYouReachThem?.includes('IN_PERSON'));

  const showSites =
    sitesEnabled &&
    showHowDidYouReachThem &&
    note.whatTypeOfPlaceWereYouIn !== '' &&
    note.whatTypeOfPlaceWereYouIn !== null &&
    ['EMERGENCY_DEPARTMENT', 'HOSPITAL', 'MEDICAL_OFFICE', 'OTHER'].includes(
      note.whatTypeOfPlaceWereYouIn
    );

  const showZCodes = note.typeOfSupport?.length > 0 && !note.typeOfSupport?.includes('NONE');

  return (
    <Stack mt="36px" display="flex" gap="20px">
      <Stack display="flex" flexDirection="row">
        <NoteType note={note} />
        <DateOfEncounter note={note} />
      </Stack>
      <Stack display="flex" flexDirection="row">
        <StartTime note={note} />
        <TimeSpent note={note} />
      </Stack>
      <Stack display="flex" flexDirection="row">
        <NextContact note={note} />
      </Stack>
      <div className={style.dottedLine} />
      <Stack>
        <Grid container rowSpacing={3}>
          <Grid item xs={6}>
            <WhoDidYouReach note={note} />
          </Grid>
          {/* Reached NO_ONE */}
          {showWhoWereYouTryingToReach && (
            <Grid item xs={6}>
              <WhoWereYouTryingToReach note={note} />
            </Grid>
          )}
          {showHowDidYouTryThem && (
            <Grid item xs={6}>
              <HowDidYouTryThem note={note} />
            </Grid>
          )}
          {/* Reached Someone Else */}
          {showWhichProvider && (
            <Grid item xs={6}>
              <WhichProvider note={note} />
            </Grid>
          )}
          {showOtherProviderName && (
            <Grid item xs={6}>
              <OtherProviderName note={note} />
            </Grid>
          )}
          {showHowDidYouReachThem && (
            <Grid item xs={6}>
              <HowDidYouReachThem note={note} />
            </Grid>
          )}
          {showWhatTypeOfPlaceWereYouIn && (
            <Grid item xs={6}>
              <WhatTypeOfPlaceWereYouIn note={note} />
            </Grid>
          )}
          {showSites && (
            <Grid item xs={6}>
              <Sites note={note} />
            </Grid>
          )}
        </Grid>
      </Stack>
      <div className={style.dottedLine} />
      <Stack display="flex" flexDirection="row">
        <TypeOfSupport note={note} />
      </Stack>
      {showZCodes && (
        <Stack display="flex" flexDirection="row">
          <ZCodes note={note} />
        </Stack>
      )}
      <div className={style.dottedLine} />
      <Stack display="flex" flexDirection="row">
        <Stack display="flex" gap="8px" flex="1">
          <p className={style.label}>Note:</p>
          <p className={style.text}>{note.note}</p>
        </Stack>
      </Stack>
      <DocumentationTime note={note} />

      <Stack display="flex" flexDirection="column">
        <Stack display="flex" flexDirection="column" gap="10px">
          {enableAiScribe && note.aiScribe?.transcript && <Transcript note={note} />}
          <Stack display="flex" flexDirection="column" gap="28px">
            {addendums.map((addendum) => (
              <Addendum key={addendum.id} addendum={addendum} />
            ))}
            {!showAddendum && (
              <Stack width="200px">
                <Button
                  handleOnClick={() => setShowAddendum(true)}
                  theme="transparent"
                  text="Add Addendum"
                  size="small"
                  icon={<span className="material-icons">add</span>}
                />
              </Stack>
            )}
          </Stack>
        </Stack>
        {showAddendum && (
          <div className={clsx(style.addendumWrapper)}>
            <AddendumForm editInitial={true} hideForm={() => setShowAddendum(false)} />
          </div>
        )}
      </Stack>
    </Stack>
  );
};

export default NoteView;
